import type { DragEvent as ReactDragEvent } from "react";
import type { ChatDropZone } from "../utils/chatDropFiles";
import TextTranslate from "../utils/TextTranslate";

type Props = {
  active: boolean;
  /** Split photo/doc zones when dragging images/videos */
  splitZones: boolean;
  hoverZone: ChatDropZone | null;
  onHoverZone: (zone: ChatDropZone | null) => void;
  onDropZone: (zone: ChatDropZone, e: ReactDragEvent) => void;
};

/**
 * Telegram-style full-chat drop: blur overlay, top = photo, bottom = file.
 */
export default function ChatFileDropOverlay({
  active,
  splitZones,
  hoverZone,
  onHoverZone,
  onDropZone,
}: Props) {
  if (!active) return null;

  const zoneClass = (zone: ChatDropZone) =>
    `flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center transition-colors duration-150 ${
      hoverZone === zone
        ? "bg-[var(--theme-color,#50a2e3)]/25"
        : "bg-transparent"
    }`;

  return (
    <div
      className="pointer-events-auto absolute inset-0 z-50 flex flex-col overflow-hidden rounded-none backdrop-blur-md"
      style={{ background: "rgba(0,0,0,0.38)" }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {splitZones ? (
        <>
          <div
            className={`${zoneClass("media")} border-b border-white/20`}
            onDragEnter={() => onHoverZone("media")}
            onDragOver={(e) => {
              e.preventDefault();
              onHoverZone("media");
            }}
            onDrop={(e) => onDropZone("media", e)}
          >
            <DropGlyph kind="media" active={hoverZone === "media"} />
            <p className="text-base font-semibold text-white drop-shadow-sm sm:text-lg">
              <TextTranslate text="Send as photo" />
            </p>
            <p className="max-w-xs text-xs text-white/75 sm:text-sm">
              <TextTranslate text="Compressed like a regular chat image" />
            </p>
          </div>
          <div
            className={zoneClass("doc")}
            onDragEnter={() => onHoverZone("doc")}
            onDragOver={(e) => {
              e.preventDefault();
              onHoverZone("doc");
            }}
            onDrop={(e) => onDropZone("doc", e)}
          >
            <DropGlyph kind="doc" active={hoverZone === "doc"} />
            <p className="text-base font-semibold text-white drop-shadow-sm sm:text-lg">
              <TextTranslate text="Send as a file" />
            </p>
            <p className="max-w-xs text-xs text-white/75 sm:text-sm">
              <TextTranslate text="Original without compression" />
            </p>
          </div>
        </>
      ) : (
        <div
          className={zoneClass("doc")}
          onDragEnter={() => onHoverZone("doc")}
          onDragOver={(e) => {
            e.preventDefault();
            onHoverZone("doc");
          }}
          onDrop={(e) => onDropZone("doc", e)}
        >
          <DropGlyph kind="doc" active={hoverZone === "doc"} />
          <p className="text-base font-semibold text-white drop-shadow-sm sm:text-lg">
            <TextTranslate text="Send files" />
          </p>
          <p className="max-w-xs text-xs text-white/75 sm:text-sm">
            <TextTranslate text="Drop here to attach" />
          </p>
        </div>
      )}
    </div>
  );
}

function DropGlyph({
  kind,
  active,
}: {
  kind: ChatDropZone;
  active: boolean;
}) {
  const scale = active ? "scale-110" : "scale-100";
  if (kind === "media") {
    return (
      <div
        className={`mb-1 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/30 transition-transform duration-150 ${scale}`}
        aria-hidden
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <rect
            x="3"
            y="5"
            width="18"
            height="14"
            rx="2.5"
            stroke="currentColor"
            strokeWidth="1.7"
          />
          <circle cx="8.5" cy="10" r="1.6" fill="currentColor" />
          <path
            d="M3.5 16.5l5-4.5 3.5 3 2.5-2.5 6 5.5"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  }
  return (
    <div
      className={`mb-1 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/30 transition-transform duration-150 ${scale}`}
      aria-hidden
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path
          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M14 2v6h6"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path
          d="M12 11v6M9 14l3 3 3-3"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}
